import { createElement, memo, useRef, useEffect, forwardRef, CSSProperties, useMemo } from 'react';
import { UnthemedCheckbox } from '../../Checkbox';
import { UnthemedRadioButton } from '../../RadioButton/__internal__';
import { postfixTestId } from '../../../util/testId';
import { Icon, IconProps } from '../../Icon';
import { Button, ButtonVariant } from '../../Button';
import { ListItemProps } from './';
import { DisclosureNext } from '@sas/react-svg';
import { useTruncationTooltip } from '../../../hooks/useTruncationTooltip';
import { setRef } from '../../../util/ref-util';
import { useIconA11yInfo } from '../../Icon/useIconA11yInfo';
import { useDesignTokens, renderInlineCssVars } from '../../../theme';
import { isNullish } from '@sas/nova-core/typeguards';
import { transformTokensToCSS } from '../__internal__';
import { createUseTextLocalizer } from '../../../locale/createUseTextLocalizer';
import { useHighlightableText } from '../../../hooks/useHighlightableText';
import bundle from '../NovaComponents-ListBox-gui-icu.properties';

import cx from 'classnames';
import '../List.css';
import cn from '../List.css.json';

const useTextLocalizer = createUseTextLocalizer(bundle);

const getIndentation = (indented?: boolean | number) =>
  typeof indented === 'boolean' ? (indented ? 1 : 0) : indented;

export const ListItem = memo(
  forwardRef<HTMLLIElement, ListItemProps>(function ListItem(props, ref) {
    const localize = useTextLocalizer();
    const refLi = useRef<HTMLLIElement | null>(null);
    const textRef = useTruncationTooltip();
    const secondaryTextRef = useTruncationTooltip();
    const dt = useDesignTokens();
    const tokens: CSSProperties = transformTokensToCSS(props.index, props.tokens);
    const indentationLevel = getIndentation(props.indented);
    const getIconA11yInfo = useIconA11yInfo();

    useEffect(() => {
      props.focused && refLi.current?.focus();
    }, [props.focused]);

    const displayMode = props.displayMode;
    const className = cx(
      props.className,
      cn.item,
      props.navigation && cn.navigationItem,
      {
        [cn.itemHoverable]:
          !props.selected && displayMode !== 'checkbox' && displayMode !== 'radio',
        [cn.selected]: props.selected && displayMode !== 'checkbox' && displayMode !== 'radio',
      },
      props.focused && cn.focused,
      props.showSelectedPseudoFocus && cn.pseudoFocused,
      props.indented && cn.indented,
      props.disabled && cn.disabled,
      props.showSeparator && cn.itemTopSeparator
    );

    const selectedIndicator =
      displayMode === 'checkbox' ? (
        <span aria-hidden="true">
          <UnthemedCheckbox
            checked={props.selected}
            focusable={false}
            className={cn.itemSelectionControl}
            onPress={() => {
              if (props.disabled) return;
              props.onSelected?.({ index: props.index });
            }}
            disabled={props.disabled}
          />
        </span>
      ) : displayMode === 'radio' ? (
        <span aria-hidden="true">
          <UnthemedRadioButton
            selected={props.selected}
            className={cn.itemSelectionControl}
            disabled={props.disabled}
          />
        </span>
      ) : null;

    const showHyphen = props.concatSecondaryText && props.secondaryText;
    const text = useHighlightableText({
      text: props.text ?? '',
      className: cx(cn.highlight, props.highlightableConfig?.text?.className),
      ...props.highlightableConfig?.text,
    });
    const secondaryHighlightableText = useHighlightableText({
      text: props.secondaryText ?? '',
      className: cx(cn.highlight, props.highlightableConfig?.secondaryText?.className),
      ...props.highlightableConfig?.secondaryText,
    });
    const secondaryText = props.secondaryText ? (
      <span
        data-testid={postfixTestId(props.testId, 'secondaryText')}
        className={cn.itemSecondaryText}
        ref={secondaryTextRef}
      >
        {secondaryHighlightableText}
      </span>
    ) : null;

    const itemTextSeparator = '-';
    const iconProps = useMemo(() => {
      if (!props.icon) return;
      return {
        testId: postfixTestId(props.testId, 'icon'),
        icon: props.icon,
        tooltip: props.iconTooltip,
        ariaLabel: props.iconAriaLabel,
      };
    }, [props.icon, props.iconAriaLabel, props.iconTooltip, props.testId]);
    const ariaLabel = useMemo(() => {
      const label = [];
      // level
      if (!isNullish(indentationLevel))
        label.push(
          localize('indentation.level.msg', {
            level: `${indentationLevel + 1}`,
          })
        );

      // icon
      const getIconDescription = (icon: IconProps) => {
        const { ariaLabel, tooltip } = getIconA11yInfo({
          ...icon,
        });
        return ariaLabel ?? tooltip;
      };
      const accessibleIcon = iconProps ? getIconDescription(iconProps) : undefined;
      if (accessibleIcon) label.push(accessibleIcon);

      // accessible text
      if (props.ariaLabel) {
        label.push(props.ariaLabel);
      } else {
        if (props.text) label.push(props.text);
        if (props.secondaryText) label.push(props.secondaryText);
      }

      // tooltip
      if (props.tooltip) label.push(props.tooltip);

      return label.length !== 0 ? label.join(', ') : undefined;
    }, [
      getIconA11yInfo,
      iconProps,
      indentationLevel,
      localize,
      props.ariaLabel,
      props.secondaryText,
      props.text,
      props.tooltip,
    ]);

    return (
      <li
        id={props.id}
        role="option"
        tabIndex={props.focusable ? 0 : -1}
        aria-label={ariaLabel}
        aria-selected={!!props.selected}
        aria-disabled={props.disabled}
        aria-setsize={props['aria-setsize']}
        aria-posinset={props['aria-posinset']}
        title={props.tooltip}
        onClick={(event) => {
          if (props.disabled) {
            event.stopPropagation();
            event.preventDefault();
            return;
          }
          // HACK: Button calls preventDefault on onClick events
          if (!event.defaultPrevented) {
            props.onSelected?.({ index: props.index });
            event.preventDefault();
          }
        }}
        onKeyDown={(event) => {
          if (props.disabled && (event.key === 'Enter' || event.key === ' ')) {
            event.stopPropagation();
            event.preventDefault();
          }
        }}
        onFocus={props.onFocus}
        className={className}
        ref={(element) => {
          setRef(ref, element);
          refLi.current = element;
        }}
        data-testid={props.testId}
        style={{
          ...tokens,
          ...renderInlineCssVars(
            {
              'indentation-level': Math.max(0, !isNullish(indentationLevel) ? indentationLevel : 0),
            },
            'list-item'
          ),
          ...props.style,
        }}
      >
        <div className={cn.itemInner}>
          {selectedIndicator}
          {iconProps && (
            <Icon
              {...iconProps}
              className={cn.itemIcon}
              tokens={() => {
                return {
                  fg:
                    props.selected && dt.themeVariant === 'high-contrast'
                      ? 'var(--list-item-selected-fg)'
                      : undefined,
                };
              }}
              disabled={props.disabled}
            />
          )}
          <span
            className={cn.itemText}
            data-testid={postfixTestId(props.testId, 'text')}
            ref={textRef}
          >
            {text}
          </span>
          {showHyphen ? (
            <span aria-hidden className={cn.itemTextSeparator}>
              {itemTextSeparator}
            </span>
          ) : null}
          {props.concatSecondaryText ? secondaryText : null}
          <div className={cn.itemSpacer} />
          {!props.concatSecondaryText ? secondaryText : null}
          {
            // note this is deprecated but kept here for backward compatibility
            props.actionIcon && (
              <Button
                className={cn.itemActionIcon}
                variant={ButtonVariant.Icon}
                onPress={() => {
                  if (props.disabled) return;
                  props.onActionPress?.({ index: props.index });
                }}
                focusable={false}
                icon={{ className: cn.itemIcon, icon: props.actionIcon }}
              />
            )
          }
          {props.navigation && (
            <Icon className={cn.navigationIcon} icon={DisclosureNext} disabled={props.disabled} />
          )}
        </div>
      </li>
    );
  })
);
